import * as OBC from "@thatopen/components";
import type * as FRAGS from "@thatopen/fragments";
import * as THREE from "three";
import { getActiveCamera, renderer, scene } from "../viewer/scene";

let components: OBC.Components | null = null;
let fragments: OBC.FragmentsManager | null = null;
let ifcLoader: OBC.IfcLoader | null = null;
let readyPromise: Promise<void> | null = null;

// Качество графики Fragments: 0 — максимально агрессивный LOD/куллинг (полная
// геометрия появляется только вплотную, мелкие детали приходится «доезжать»);
// 1 — полная геометрия видна далеко, но тяжелее для GPU и памяти на крупных
// моделях. Внутри библиотеки значение задаёт порог переключения на LOD:
//   view.graphicQuality = graphicsQuality * -1.5 + 2  (чем больше — тем дальше
//   рендерится полная геометрия). Дефолт библиотеки = 0; поднимаем умеренно,
// чтобы детали прогружались на большем расстоянии без чрезмерной нагрузки.
const GRAPHICS_QUALITY = 0.5;

function wasmBaseUrl(): string {
  return new URL(`${import.meta.env.BASE_URL}ifc-wasm/`, window.location.origin).toString();
}

function fragmentsWorkerUrl(): string {
  return new URL(`${import.meta.env.BASE_URL}fragments-worker/worker.mjs`, window.location.origin).toString();
}

export function getFragmentsComponents(): OBC.Components {
  if (!components) {
    throw new Error("Fragments engine is not initialized");
  }
  return components;
}

export function getFragmentsManager(): OBC.FragmentsManager {
  if (!fragments) {
    throw new Error("FragmentsManager is not initialized");
  }
  return fragments;
}

export function getFragmentsIfcLoader(): OBC.IfcLoader {
  if (!ifcLoader) {
    throw new Error("IfcLoader is not initialized");
  }
  return ifcLoader;
}

export function getActiveFragmentsModel(modelId: string): FRAGS.FragmentsModel | undefined {
  return getFragmentsManager().list.get(modelId);
}

export function initFragmentsEngine(): Promise<void> {
  if (!readyPromise) {
    readyPromise = (async () => {
      components = new OBC.Components();
      fragments = components.get(OBC.FragmentsManager);
      fragments.init(fragmentsWorkerUrl());
      // Глобально для всех будущих моделей: при загрузке модель копирует это
      // значение из settings (model.graphicsQuality = settings.graphicsQuality).
      fragments.core.settings.graphicsQuality = GRAPHICS_QUALITY;

      ifcLoader = components.get(OBC.IfcLoader);
      await ifcLoader.setup({
        autoSetWasm: false,
        wasm: {
          path: wasmBaseUrl(),
          absolute: true,
        },
      });

      fragments.list.onItemSet.add(({ value: model }) => {
        // Дублируем на саму модель — гарантия, что LOD-порог применится даже
        // если модель пришла не через стандартный load() (см. GRAPHICS_QUALITY).
        model.graphicsQuality = GRAPHICS_QUALITY;
        model.useCamera(getActiveCamera());
        // Fragments отсекает геометрию во воркере и берёт плоскости отсюда
        // (renderer.clippingPlanes как единый источник). Без этого callback
        // глобальные clippingPlanes на кастомные шейдеры Fragments не действуют.
        model.getClippingPlanesEvent = () => renderer.clippingPlanes;
        if (!model.object.parent) {
          scene.add(model.object);
        }
      });
    })().catch((error) => {
      readyPromise = null;
      throw error;
    });
  }
  return readyPromise;
}

export async function loadFragmentsBuffer(
  buffer: ArrayBuffer,
  modelId: string,
  onProgress?: (pct: number, label: string) => void,
): Promise<FRAGS.FragmentsModel> {
  await initFragmentsEngine();
  const mgr = getFragmentsManager();
  const existing = mgr.list.get(modelId);
  if (existing) {
    await mgr.core.disposeModel(modelId);
  }

  onProgress?.(0, "Загрузка Fragments…");
  const model = await mgr.core.load(buffer, {
    modelId,
    camera: getActiveCamera(),
    onProgress: (event) => {
      if (!event?.progress) {
        return;
      }
      const pct = Math.round(event.progress * 100);
      onProgress?.(pct, `Загрузка Fragments — ${pct}%`);
    },
  });
  onProgress?.(100, "Fragments загружен");
  return model;
}

export async function loadIfcAsFragments(
  bytes: Uint8Array,
  modelId: string,
  onProgress?: (pct: number, label: string) => void,
): Promise<FRAGS.FragmentsModel> {
  await initFragmentsEngine();
  const loader = getFragmentsIfcLoader();
  const mgr = getFragmentsManager();
  const existing = mgr.list.get(modelId);
  if (existing) {
    await mgr.core.disposeModel(modelId);
  }

  onProgress?.(0, "Конвертация IFC → Fragments…");
  const model = await loader.load(bytes, true, modelId, {
    processData: {
      progressCallback: (progress, data) => {
        const pct = Math.round((progress ?? 0) * 100);
        const stage = typeof data === "string" ? data : "конвертация";
        onProgress?.(pct, `Конвертация IFC — ${pct}% (${stage})`);
      },
    },
  });
  onProgress?.(100, "IFC сконвертирован");
  return model;
}

export async function disposeFragmentsModel(modelId: string | null | undefined): Promise<void> {
  if (!modelId || !fragments) {
    return;
  }
  if (!fragments.list.has(modelId)) {
    return;
  }
  await fragments.core.disposeModel(modelId);
}

export function setFragmentsCamera(next: THREE.PerspectiveCamera | THREE.OrthographicCamera): void {
  if (!fragments) {
    return;
  }
  for (const model of fragments.list.values()) {
    model.useCamera(next);
  }
}

export function updateFragmentsView(force = false): Promise<void> {
  if (!fragments?.initialized) {
    return Promise.resolve();
  }
  return fragments.core.update(force);
}

export function frameFragmentsObject(object: THREE.Object3D): THREE.Box3 {
  const box = new THREE.Box3().setFromObject(object);
  return box;
}
